import { AfterViewInit, Component, ElementRef, OnInit, Optional, Renderer2, ViewChild } from '@angular/core';
import { MmDialogRefService } from '@metromarkets/components';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil, withLatestFrom } from 'rxjs/operators';
import { State } from '~app/modules/checkout/store';
import { environment } from '~env/environment';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { AdyenTranslation } from '../../constants';
import {
  ChallengeActionType,
  ChallengeAuthResult,
  ChallengeResponseAction,
  PaymentMethodConfig,
  ServicePaymentMethodTypes,
} from '../../models';
import { Payment3dsService } from '../../services';
import {
  Adyen3DSDetails,
  CancelAdyen3DSFlow,
  CancelSofortFlow,
  InitAdyen3DSFlow,
  InitSofortFlow,
  PaymentActionTypes,
} from '../../store/payment/payment.actions';
import { PaymentUtils } from '../../utils/payment-util';
import { CheckoutStateSharedKeys, CheckoutStateSharedService } from '../../services/checkout-state-shared.service';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';

declare const AdyenCheckout;

@Component({
  selector: 'app-adyen-3ds-dialog',
  templateUrl: './adyen-3ds-dialog.component.html',
  styleUrls: ['./adyen-3ds-dialog.component.scss'],
})
export class Adyen3DSDialogComponent implements AfterViewInit, OnInit {
  @ViewChild('adyenDropinContainer') adyenDropinContainer: ElementRef<HTMLElement>;
  private checkout: any;
  private adyenConfig: PaymentMethodConfig;
  private challengeResponseAction: ChallengeResponseAction;
  private orderNumber: string;
  private readonly widgetCssSelector = '#adyen-dropin';
  private waitForFingerprintResults: boolean;
  private unsubscribe$ = new Subject<void>();
  paymentMethodSelected: string;
  private paymentMethodOptions = ServicePaymentMethodTypes;

  public isAdyenLoading = true;
  public challengeType;

  constructor(
    @Optional() public dialogRef: MmDialogRefService<any>,
    private store$: Store<State>,
    private actions$: Actions,
    private renderer: Renderer2,
    private payment3DSService: Payment3dsService,
    private serverLogger: ServerLoggerService
  ) {
    this.adyenConfig = this.dialogRef.containerInstance.config.data.adyenConfig;
    this.initConfiguration();
  }

  ngOnInit() {
    this.subscribeToFingerprintResults();
    this.subscribeToRechallenge();
    this.subscribeToFailure();
  }

  ngAfterViewInit() {
    this.initiateAdyen();
    this.handleAdyenAction();
  }

  closeDialog(cancel = false) {
    const orderNumber = CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage();
    this.serverLogger.info(`Checkout - FE - ${orderNumber} 3DS Dialog closed`, {
      paymentMethod: this.paymentMethodSelected,
    });

    this.dialogRef.close(cancel);

    if (cancel && this.paymentMethodSelected === this.paymentMethodOptions.CreditCard) {
      this.store$.dispatch(new CancelAdyen3DSFlow());
    } else if (cancel && this.paymentMethodSelected === this.paymentMethodOptions.DirectEbanking) {
      this.store$.dispatch(new CancelSofortFlow());
    }
  }

  subscribeToFailure() {
    this.actions$
      .pipe(ofType(PaymentActionTypes.MAKE_PAYMENT_OTHER_FAILURE), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.closeDialog();
        this.payment3DSService.setWaitForFingerprintResults(false);
      });
  }

  initConfiguration(config?): void {
    const data = config || this.dialogRef.containerInstance.config.data;
    this.challengeResponseAction = data.adyenResponseAction;
    this.orderNumber = data.orderNumber;
    this.challengeType = this.challengeResponseAction.type;
    this.paymentMethodSelected = this.challengeResponseAction.paymentMethodType;

    if (this.challengeType === ChallengeActionType.Fingerprint) {
      this.payment3DSService.setWaitForFingerprintResults(true);
      this.waitForFingerprintResults = true;
    } else {
      this.payment3DSService.setWaitForFingerprintResults(false);
      this.waitForFingerprintResults = false;
      this.isAdyenLoading = false;
    }

    const orderNumber = CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage();

    this.serverLogger.info(`Checkout - FE - ${orderNumber} Init 3DS dialog configuration`, {
      orderNumber: this.orderNumber,
      challengeType: this.challengeType,
      paymentMethodSelected: this.paymentMethodSelected,
      waitForFingerprintResults: this.waitForFingerprintResults,
    });
  }

  subscribeToFingerprintResults(): void {
    this.actions$
      .pipe(ofType(PaymentActionTypes.MAKE_PAYMENT_SUCCESS))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const orderNumber = CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage();
        this.serverLogger.info(`Checkout - FE - ${orderNumber} Fingerprint Challenge success`, {
          orderNumber: this.orderNumber,
        });

        this.isAdyenLoading = false;
        this.closeDialog();
      });
  }

  subscribeToRechallenge(): void {
    this.callPaymentOverlay(false);
    this.actions$
      .pipe(
        ofType(PaymentActionTypes.INIT_ADYEN_3DS_FLOW, PaymentActionTypes.INIT_SOFORT_FLOW),
        withLatestFrom(
          this.payment3DSService.waitForFingerprintResults$,
          (action: InitAdyen3DSFlow | InitSofortFlow, isWaitEnabled: boolean) => ({ action, isWaitEnabled })
        ),
        filter(({ isWaitEnabled }) => isWaitEnabled)
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(({ action }) => {
        const orderNumber = CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage();
        this.serverLogger.info(`Checkout - FE - ${orderNumber} 3DS Rechallenge`, {
          orderNumber: action.payload.orderNumber,
        });

        this.initConfiguration({
          orderNumber: action.payload.orderNumber,
          adyenResponseAction: action.payload.challengeResponse.action,
          challengeType: this.challengeType,
        });

        this.handleAdyenAction();
      });
  }

  initiateAdyen() {
    const adyenWidget = this.renderer.createElement('div');
    this.renderer.setProperty(adyenWidget, 'id', 'adyen-dropin');
    this.renderer.appendChild(this.adyenDropinContainer.nativeElement, adyenWidget);
    this.checkout = new AdyenCheckout({
      environment: environment.adyenEnv(),
      locale: environment.locale(),
      translations: AdyenTranslation,
      ...PaymentUtils.getKeyConfig(this.adyenConfig?.options || {}),
      onAdditionalDetails: ({ data }: { data: ChallengeAuthResult }) => {
        const orderNumber = CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage();
        this.serverLogger.info(`Checkout - FE - ${orderNumber} Challenge Auth Result`, {
          ...data,
          orderNumber: this.orderNumber,
        });

        this.store$.dispatch(new Adyen3DSDetails({ orderNumber: this.orderNumber, details: data.details }));
        if (!this.waitForFingerprintResults) {
          this.closeDialog();
          this.callPaymentOverlay(true);
        }
      },
    });
  }

  handleAdyenAction() {
    try {
      this.checkout.createFromAction(this.challengeResponseAction).mount(this.widgetCssSelector);
    } catch (e) {
      //catch instead of force check if element is present or not in DOM.
    }
  }

  private callPaymentOverlay(flag: boolean) {
    const handlePaymenyProcessingOverlay = CheckoutStateSharedService.get(
      CheckoutStateSharedKeys.HandlePaymentProcessingCb
    );
    if (typeof handlePaymenyProcessingOverlay === 'function') {
      handlePaymenyProcessingOverlay(flag);
    }
  }
}
